import type { ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { useTenant } from '../contexts/TenantContext';
import { canAccessPersona, getPersonaFromPath } from '../utils/accessControl';
import { ForbiddenPage } from './ForbiddenPage';

interface ProtectedRouteProps {
  children: ReactNode;
}

/**
 * Route protection wrapper that checks if the user has access
 * to the current persona route based on their roles.
 *
 * Used to wrap persona routes (/self-care, /insights, /admin) in App.tsx.
 */
export const ProtectedRoute = ({ children }: ProtectedRouteProps) => {
  const location = useLocation();
  const { currentTenant, loading } = useTenant();

  // Show loading while tenant context initializes
  if (loading) {
    return <div>Loading...</div>;
  }

  // If no tenant selected, let the route handle it (e.g., redirect to tenant selection)
  if (!currentTenant) {
    return <>{children}</>;
  }

  // Get persona from current path
  const persona = getPersonaFromPath(location.pathname);

  // If on a persona route, check access
  if (persona && !canAccessPersona(currentTenant.roles, persona)) {
    return <ForbiddenPage />;
  }

  return <>{children}</>;
};

export default ProtectedRoute;
